// Lightweight, zero-dependency OpenXML (.xlsx) generator and parser
// Eliminates Excel "file format and extension don't match" warnings.

function makeCrc32Table(): Uint32Array {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) {
      c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
    }
    table[i] = c >>> 0;
  }
  return table;
}

const crcTable = makeCrc32Table();

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    crc = crcTable[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function colLetter(colIdx: number): string {
  let res = '';
  let n = colIdx + 1;
  while (n > 0) {
    const rem = (n - 1) % 26;
    res = String.fromCharCode(65 + rem) + res;
    n = Math.floor((n - 1) / 26);
  }
  return res;
}

function escapeXml(str: any): string {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

export interface ZipEntry {
  name: string;
  data: Uint8Array;
}

export function createStoredZip(files: ZipEntry[]): Uint8Array {
  const encoder = new TextEncoder();
  let totalLocalSize = 0;
  let totalCdSize = 0;

  const fileInfos = files.map((f) => {
    const nameBytes = encoder.encode(f.name);
    const crc = crc32(f.data);
    const localHeaderSize = 30 + nameBytes.length;
    const cdHeaderSize = 46 + nameBytes.length;
    totalLocalSize += localHeaderSize + f.data.length;
    totalCdSize += cdHeaderSize;
    return { ...f, nameBytes, crc };
  });

  const eocdSize = 22;
  const buffer = new Uint8Array(totalLocalSize + totalCdSize + eocdSize);
  const view = new DataView(buffer.buffer);

  let offset = 0;
  const cdOffsets: number[] = [];

  // 1. Write Local File Headers & Data
  for (const f of fileInfos) {
    cdOffsets.push(offset);

    // Signature 0x04034b50
    view.setUint32(offset, 0x04034b50, true);
    view.setUint16(offset + 4, 20, true);
    view.setUint16(offset + 6, 0x0800, true); // UTF-8 filename
    view.setUint16(offset + 8, 0, true); // stored (no compression)
    view.setUint16(offset + 10, 0, true);
    view.setUint16(offset + 12, 0, true);
    view.setUint32(offset + 14, f.crc, true);
    view.setUint32(offset + 18, f.data.length, true);
    view.setUint32(offset + 22, f.data.length, true);
    view.setUint16(offset + 26, f.nameBytes.length, true);
    view.setUint16(offset + 28, 0, true);

    buffer.set(f.nameBytes, offset + 30);
    buffer.set(f.data, offset + 30 + f.nameBytes.length);

    offset += 30 + f.nameBytes.length + f.data.length;
  }

  // 2. Write Central Directory Headers
  const cdStartOffset = offset;
  for (let i = 0; i < fileInfos.length; i++) {
    const f = fileInfos[i];
    const localOffset = cdOffsets[i];

    // Signature 0x02014b50
    view.setUint32(offset, 0x02014b50, true);
    view.setUint16(offset + 4, 20, true);
    view.setUint16(offset + 6, 20, true);
    view.setUint16(offset + 8, 0x0800, true);
    view.setUint16(offset + 10, 0, true);
    view.setUint16(offset + 12, 0, true);
    view.setUint16(offset + 14, 0, true);
    view.setUint32(offset + 16, f.crc, true);
    view.setUint32(offset + 20, f.data.length, true);
    view.setUint32(offset + 24, f.data.length, true);
    view.setUint16(offset + 28, f.nameBytes.length, true);
    view.setUint16(offset + 30, 0, true);
    view.setUint16(offset + 32, 0, true);
    view.setUint16(offset + 34, 0, true);
    view.setUint16(offset + 36, 0, true);
    view.setUint32(offset + 38, 0, true);
    view.setUint32(offset + 42, localOffset, true);

    buffer.set(f.nameBytes, offset + 46);
    offset += 46 + f.nameBytes.length;
  }

  // 3. Write End of Central Directory
  view.setUint32(offset, 0x06054b50, true);
  view.setUint16(offset + 4, 0, true);
  view.setUint16(offset + 6, 0, true);
  view.setUint16(offset + 8, files.length, true);
  view.setUint16(offset + 10, files.length, true);
  view.setUint32(offset + 12, totalCdSize, true);
  view.setUint32(offset + 16, cdStartOffset, true);
  view.setUint16(offset + 20, 0, true);

  return buffer;
}

export function generateXlsxBlob(sheetName: string, rows: (string | number)[][]): Blob {
  const encoder = new TextEncoder();

  let sheetXml = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';
  sheetXml += '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>';

  for (let r = 0; r < rows.length; r++) {
    const rowNum = r + 1;
    sheetXml += `<row r="${rowNum}">`;
    const row = rows[r];
    for (let c = 0; c < row.length; c++) {
      const val = row[c];
      const cellRef = `${colLetter(c)}${rowNum}`;
      if (typeof val === 'number' && !isNaN(val)) {
        sheetXml += `<c r="${cellRef}"><v>${val}</v></c>`;
      } else {
        sheetXml += `<c r="${cellRef}" t="inlineStr"><is><t>${escapeXml(val)}</t></is></c>`;
      }
    }
    sheetXml += '</row>';
  }
  sheetXml += '</sheetData></worksheet>';

  const contentTypesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
</Types>`;

  const relsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

  const workbookRelsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
</Relationships>`;

  const workbookXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>
    <sheet name="${escapeXml(sheetName || 'Sheet1')}" sheetId="1" r:id="rId1"/>
  </sheets>
</workbook>`;

  const files: ZipEntry[] = [
    { name: '[Content_Types].xml', data: encoder.encode(contentTypesXml) },
    { name: '_rels/.rels', data: encoder.encode(relsXml) },
    { name: 'xl/_rels/workbook.xml.rels', data: encoder.encode(workbookRelsXml) },
    { name: 'xl/workbook.xml', data: encoder.encode(workbookXml) },
    { name: 'xl/worksheets/sheet1.xml', data: encoder.encode(sheetXml) },
  ];

  const zipBytes = createStoredZip(files);
  return new Blob([zipBytes as any], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

// Multi-sheet workbook generator for full reports
export interface XlsxSheet {
  name: string;
  rows: (string | number)[][];
}

export function generateMultiSheetXlsxBlob(sheets: XlsxSheet[]): Blob {
  const encoder = new TextEncoder();
  const safeSheets = sheets.length > 0 ? sheets : [{ name: 'Sheet1', rows: [] }];

  let typesOverrides = '';
  let workbookRels = '';
  let workbookSheets = '';
  const files: ZipEntry[] = [];

  for (let i = 0; i < safeSheets.length; i++) {
    const s = safeSheets[i];
    const sheetId = i + 1;
    const rId = `rId${sheetId}`;
    const partPath = `worksheets/sheet${sheetId}.xml`;

    typesOverrides += `<Override PartName="/xl/${partPath}" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`;
    workbookRels += `<Relationship Id="${rId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="${partPath}"/>`;
    workbookSheets += `<sheet name="${escapeXml(s.name)}" sheetId="${sheetId}" r:id="${rId}"/>`;

    let sheetXml = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>';
    for (let r = 0; r < s.rows.length; r++) {
      const rowNum = r + 1;
      sheetXml += `<row r="${rowNum}">`;
      const row = s.rows[r];
      for (let c = 0; c < row.length; c++) {
        const val = row[c];
        const cellRef = `${colLetter(c)}${rowNum}`;
        if (typeof val === 'number' && !isNaN(val)) {
          sheetXml += `<c r="${cellRef}"><v>${val}</v></c>`;
        } else {
          sheetXml += `<c r="${cellRef}" t="inlineStr"><is><t>${escapeXml(val)}</t></is></c>`;
        }
      }
      sheetXml += '</row>';
    }
    sheetXml += '</sheetData></worksheet>';

    files.push({ name: `xl/${partPath}`, data: encoder.encode(sheetXml) });
  }

  const contentTypesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  ${typesOverrides}
</Types>`;

  const relsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

  const workbookRelsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  ${workbookRels}
</Relationships>`;

  const workbookXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>${workbookSheets}</sheets>
</workbook>`;

  files.unshift(
    { name: '[Content_Types].xml', data: encoder.encode(contentTypesXml) },
    { name: '_rels/.rels', data: encoder.encode(relsXml) },
    { name: 'xl/_rels/workbook.xml.rels', data: encoder.encode(workbookRelsXml) },
    { name: 'xl/workbook.xml', data: encoder.encode(workbookXml) }
  );

  const zipBytes = createStoredZip(files);
  return new Blob([zipBytes as any], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

// ===== PARSE INCOMING .XLSX BINARY FILE =====
export async function extractSheetXmlFromXlsx(buffer: ArrayBuffer): Promise<{
  sheetXml: string;
  sharedStrings: string[];
}> {
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);
  let offset = 0;
  let rawSheetXml: Uint8Array | null = null;
  let rawSharedStrings: Uint8Array | null = null;
  let sheetMethod = 0;
  let ssMethod = 0;

  while (offset < bytes.length - 30) {
    const sig = view.getUint32(offset, true);
    if (sig !== 0x04034b50) break; // End of local files

    const compMethod = view.getUint16(offset + 8, true);
    const compSize = view.getUint32(offset + 18, true);
    const nameLen = view.getUint16(offset + 26, true);
    const extraLen = view.getUint16(offset + 28, true);

    const nameBytes = bytes.subarray(offset + 30, offset + 30 + nameLen);
    const name = new TextDecoder().decode(nameBytes);
    const dataStart = offset + 30 + nameLen + extraLen;
    const fileBytes = bytes.subarray(dataStart, dataStart + compSize);

    if (name.includes('sheet1.xml')) {
      rawSheetXml = fileBytes;
      sheetMethod = compMethod;
    } else if (name.includes('sharedStrings.xml')) {
      rawSharedStrings = fileBytes;
      ssMethod = compMethod;
    }

    offset = dataStart + compSize;
  }

  const decompress = async (data: Uint8Array, method: number): Promise<string> => {
    if (method === 0) {
      return new TextDecoder().decode(data);
    }
    const GlobalDecompressionStream = (globalThis as any).DecompressionStream;
    if (typeof GlobalDecompressionStream !== 'undefined') {
      try {
        const ds = new GlobalDecompressionStream('deflate-raw');
        const writer = ds.writable.getWriter();
        writer.write(data as any);
        writer.close();
        const resp = new Response(ds.readable);
        return await resp.text();
      } catch (e) {
        console.warn('DecompressionStream error:', e);
      }
    }
    return new TextDecoder().decode(data);
  };

  let sheetXml = '';
  if (rawSheetXml) {
    sheetXml = await decompress(rawSheetXml, sheetMethod);
  }

  const sharedStrings: string[] = [];
  if (rawSharedStrings) {
    const ssXml = await decompress(rawSharedStrings, ssMethod);
    const parser = new DOMParser();
    const doc = parser.parseFromString(ssXml, 'text/xml');
    const siElements = doc.getElementsByTagName('si');
    for (let i = 0; i < siElements.length; i++) {
      sharedStrings.push(siElements[i].textContent || '');
    }
  }

  return { sheetXml, sharedStrings };
}
